// Daily nutrient panel — shows 25 vitamins, minerals, and "other" nutrients
import PageTitle from "../components/PageTitle";
// vs the FDA/NIH RDA for adults. Aggregates across all of today's logged meals.
//
// Data quality varies by source:
//   - AI photo + text estimates: returns nutrients when the model is confident
//   - Open Food Facts barcode lookups: pulled from the label when present
//   - Curated generic foods: not yet populated (shown as missing)
//
// Missing values show as "—" rather than "0" so the user knows the data
// isn't there (vs. genuinely zero).

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { loadMealsRemote, mealsOnDate, todayISO } from "../lib/storage";
import {
  NUTRIENT_GROUPS,
  NUTRIENT_LABELS,
  RDA,
  coverageCount,
  formatNutrient,
  nutrientSourcesForDay,
  percentOfRDA,
  sumDayNutrients,
  type NutrientPanel,
  type NutrientSource
} from "../lib/nutrients";
import { MEAL_TYPE_ICONS, type LoggedMeal } from "../lib/types";
import { friendlyError } from "../lib/errors";

type Tab = keyof typeof NUTRIENT_GROUPS;
const TABS: Tab[] = ["Vitamins", "Minerals", "Other"];

export default function Nutrients() {
  const navigate = useNavigate();
  const [meals, setMeals] = useState<LoggedMeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("Vitamins");
  const [date] = useState(todayISO());
  /** Which nutrient row is currently expanded to show source contributions.
   *  Null = none. Only one open at a time keeps the page tidy. */
  const [openNutrient, setOpenNutrient] = useState<keyof NutrientPanel | null>(null);

  useEffect(() => {
    loadMealsRemote()
      .then(setMeals)
      .catch((err) => setError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, []);

  const todayMeals = useMemo(() => mealsOnDate(meals, date), [meals, date]);
  const dayPanel = useMemo(() => sumDayNutrients(todayMeals), [todayMeals]);
  const coverage = useMemo(() => coverageCount(dayPanel), [dayPanel]);
  const itemsWithNutrients = useMemo(
    () =>
      todayMeals.flatMap((m) => m.estimate.items.filter((i) => i.nutrients)).length,
    [todayMeals]
  );
  const totalItems = useMemo(
    () => todayMeals.flatMap((m) => m.estimate.items).length,
    [todayMeals]
  );

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-sm text-slate-400">
          ← Back
        </button>
        <PageTitle>Nutrients</PageTitle>
      </div>

      <p className="text-xs text-slate-400">
        Today's vitamins and minerals as a percent of what most adults need (FDA daily values). Sodium, sugar, and saturated fat show how close you are to the daily <span className="text-slate-300">upper limit</span> — lower is better.
      </p>

      {loading ? (
        <div className="text-center text-slate-400 py-12 text-sm">Loading nutrients…</div>
      ) : error ? (
        <div className="p-3 rounded-lg bg-red-950 border border-red-800 text-red-200 text-sm">
          {error}
        </div>
      ) : (
        <>
          {/* Coverage banner — sets expectations on data quality */}
          <div className="p-3 rounded-2xl bg-slate-900 border border-slate-800 text-sm">
            <div className="flex items-baseline justify-between">
              <span className="text-slate-200 font-medium">{coverage} of 25 nutrients</span>
              <span className="text-xs text-slate-400">
                {itemsWithNutrients}/{totalItems} {totalItems === 1 ? "item" : "items"} with data
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {coverage < 5
                ? "Most foods today don't have nutrient data. Log a meal via photo or barcode to fill in vitamins + minerals."
                : "Some foods may show as missing (—). AI estimates and packaged barcode scans return the richest data."}
            </p>
          </div>

          {/* Tab bar */}
          <div className="flex bg-slate-900 border border-slate-800 rounded-lg p-1">
            {TABS.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                aria-pressed={tab === t}
                className={`flex-1 py-2 rounded text-sm font-medium ${
                  tab === t ? "bg-slate-800 text-emerald-400" : "text-slate-400"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Nutrient rows for the active tab */}
          <div className="rounded-2xl bg-slate-900 border border-slate-800 divide-y divide-slate-800/60 overflow-hidden">
            {NUTRIENT_GROUPS[tab].map((key) => (
              <NutrientRow
                key={key}
                nutrientKey={key}
                panel={dayPanel}
                meals={todayMeals}
                expanded={openNutrient === key}
                onToggle={() => setOpenNutrient(openNutrient === key ? null : key)}
                onPickMeal={(mealId) => navigate(`/meal/${mealId}`)}
              />
            ))}
          </div>

          <p className="text-[11px] text-slate-500 text-center pt-2">
            RDA values are general adult guidance from NIH / FDA. Consult a registered dietitian for personalized targets.
          </p>
        </>
      )}
    </div>
  );
}

function NutrientRow({
  nutrientKey,
  panel,
  meals,
  expanded,
  onToggle,
  onPickMeal
}: {
  nutrientKey: keyof NutrientPanel;
  panel: NutrientPanel;
  meals: LoggedMeal[];
  expanded: boolean;
  onToggle: () => void;
  onPickMeal: (mealId: string) => void;
}) {
  const value = panel[nutrientKey];
  const pct = percentOfRDA(nutrientKey, value);
  const rda = RDA[nutrientKey];
  const isUpperLimit =
    nutrientKey === "sodium_mg" || nutrientKey === "sugar_g" || nutrientKey === "saturated_fat_g";

  // Per-item contribution list — lazy-built so we don't pay the cost when
  // the row is collapsed. The dependency array is fine here because both
  // `meals` and `nutrientKey` come from the parent and are stable per render.
  const sources = useMemo<NutrientSource[]>(
    () => (expanded ? nutrientSourcesForDay(meals, nutrientKey) : []),
    [expanded, meals, nutrientKey]
  );

  const barWidth = pct === null ? 0 : Math.min(100, pct);
  let barColor = "bg-slate-700";
  if (pct !== null && pct > 0) {
    if (isUpperLimit) {
      barColor = pct > 100 ? "bg-rose-500" : pct > 75 ? "bg-amber-500" : "bg-emerald-500";
    } else {
      barColor = pct >= 80 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-rose-500";
    }
  }

  const hasData = value !== undefined && Number.isFinite(value) && value > 0;

  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        // Disable expand when there's no data — there'd be nothing to show
        // and the empty list reads as a bug.
        disabled={!hasData}
        className="w-full text-left px-4 py-3 active:bg-slate-800/40 disabled:active:bg-transparent"
        aria-expanded={expanded}
      >
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-sm text-slate-200 flex items-center gap-1.5">
            {NUTRIENT_LABELS[nutrientKey]}
            {hasData && (
              <ChevronDown
                className={`w-3.5 h-3.5 text-slate-500 transition-transform ${
                  expanded ? "rotate-180" : ""
                }`}
              />
            )}
          </span>
          <span className="text-xs text-slate-400 tabular-nums">
            <span className="text-slate-200 font-medium">{formatNutrient(nutrientKey, value)}</span>
            <span className="text-slate-500"> / {formatNutrient(nutrientKey, rda)}</span>
          </span>
        </div>
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
          <div
            className={`h-full ${barColor} transition-all`}
            style={{ width: `${barWidth}%` }}
          />
        </div>
        <div className="text-[10px] text-slate-500 mt-1 tabular-nums">
          {pct === null ? (
            <span className="text-slate-600">no data</span>
          ) : (
            <>
              {pct}% of {isUpperLimit ? "upper limit" : "RDA"}
              {hasData && <span className="text-slate-600"> · tap for sources</span>}
            </>
          )}
        </div>
      </button>

      {expanded && hasData && (
        <div className="px-4 pb-3 -mt-1 space-y-1.5">
          {sources.length === 0 ? (
            <div className="text-[11px] text-slate-500 italic py-2">
              No per-item source data on file. Try logging via AI photo / barcode for richer breakdown.
            </div>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 pt-1">
                Where it came from
              </div>
              {sources.slice(0, 8).map((s, i) => (
                <button
                  type="button"
                  key={`${s.mealId}-${i}`}
                  onClick={() => onPickMeal(s.mealId)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg bg-slate-800/60 hover:bg-slate-800 text-left active:bg-slate-800/80"
                >
                  <span className="text-base flex-shrink-0" aria-hidden="true">
                    {MEAL_TYPE_ICONS[s.mealType]}
                  </span>
                  <span className="flex-1 min-w-0 text-xs text-slate-200 truncate">
                    {s.itemName}
                  </span>
                  <span className="text-xs text-slate-300 tabular-nums flex-shrink-0">
                    {formatNutrient(nutrientKey, s.value)}
                  </span>
                  <span className="text-[10px] text-slate-500 tabular-nums w-9 text-right">
                    {s.pctOfDay}%
                  </span>
                </button>
              ))}
              {sources.length > 8 && (
                <div className="text-[11px] text-slate-500 text-center pt-0.5">
                  + {sources.length - 8} more contributors
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
